import React, { useEffect, useRef, useState } from 'react'
import { Form, Select, Spin } from 'antd'
import { SelectValue } from 'antd/lib/select'
import { useTranslation } from 'react-i18next'

import { Response } from 'src/services/api'
import * as CurrenciesService from 'src/services/api/currencies'

import { SelectProps } from '../Select'

interface Props extends Omit<SelectProps, 'name' | 'options' | 'onChange'> {
  name?: string
  onChange: (value: CurrenciesService.ReferenceCurrencies) => void
}

export default function SelectCurrencyAvailable({
  formikProps,
  onChange
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [options, setOptions] = useState<
    CurrenciesService.ReferenceCurrencies[]
  >([])

  const { t } = useTranslation()
  const refTimeout = useRef<NodeJS.Timeout>(null)

  const errorOptions: CurrenciesService.ReferenceCurrencies = {
    code: '',
    decimal_digits: null,
    name: null,
    reference_currency_id: null
  }

  function onSearchCatch() {
    errorOptions.name = t('web.user.errors.on-search')
    setOptions([errorOptions])
  }

  function onSearchData(
    values: Response<CurrenciesService.GetAvailableCurrencyData>
  ) {
    if (values?.status === 200) {
      setOptions(values?.data?.reference_currencies || [])
      return
    }
    onSearchCatch()
  }

  useEffect(() => {
    if (formikProps?.values?.currency_id) {
      //is editing, do not fetch
      return
    }

    setIsLoading(true)
    CurrenciesService.getAvailableCurrency()
      .then(onSearchData)
      .catch(onSearchCatch)
      .finally(() => {
        setIsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onSearch(value: string) {
    clearTimeout(refTimeout.current)
    refTimeout.current = setTimeout(async () => {
      setIsLoading(true)
      CurrenciesService.getAvailableCurrency({ code: value })
        .then(onSearchData)
        .catch(onSearchCatch)
        .finally(() => {
          setIsLoading(false)
        })
    }, 1000)
  }

  const notFoundContent =
    (isLoading && <Spin size="small" />) ||
    t('web.resources.currencies.search-fail')

  function handleChange(value: SelectValue & { key: string }) {
    let optionSelected: CurrenciesService.ReferenceCurrencies = undefined

    if (value) {
      optionSelected = options.find(
        opt => opt?.reference_currency_id === Number(value?.key || -1)
      )
    }
    onChange(optionSelected)
  }

  const label = t('web.resources.currencies.list-title')
  const placeholder = t('web.resources.currencies.code-search')

  return (
    <Form.Item colon={false} label={label}>
      <Select
        allowClear
        data-test-id="currency-list"
        disabled={!!formikProps?.values?.currency_id}
        filterOption={false}
        labelInValue
        loading={isLoading}
        notFoundContent={notFoundContent}
        onChange={handleChange}
        onSearch={onSearch}
        placeholder={placeholder}
        showSearch
        style={{ width: '100%' }}
      >
        {options.map((d, index) => {
          const title = `${d.code} - ${d.name}`
          return (
            <Select.Option
              disabled={!d.code}
              key={index}
              title={title}
              value={d.reference_currency_id}
            >
              {title}
            </Select.Option>
          )
        })}
      </Select>
    </Form.Item>
  )
}
